import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { withLoadingReducer } from 'utils/reducerHandler'
import { GetPurchaseOrderReferencesResponseType } from 'features/PurchaseOrder/services/references'
import {
  GetSOProductsRequestType,
  GetSOProductsResponseType,
  GetSupplyOrderRemarksResponseType,
} from 'features/SupplyOrder/services/outboundSO'
import { GetAllLocationsResponseType } from 'features/Location/services/location'
import {
  fetchGetSOProduct,
  fetchPostSupplyOrderDetail,
  fetchPutSupplyOrderDetail,
  SLICE_NAME,
} from './thunk'

type ProductItemType = {
  productId: number
  productQrCode: string
  productRequestQuantity: number
  productStatusId: number
  productStatusNoteId: number | null
  productStock: number
}

export type OutboundSOAddProductSliceType = {
  isLoading: boolean
  checkedIds: string[]
  selectedItems: Record<string, ProductItemType>
  locationId: number
  query: GetSOProductsRequestType
  data: GetSOProductsResponseType['data']['content']
  createSO: {
    poReff?: Nullable<GetPurchaseOrderReferencesResponseType['data']['content'][number]>
    soReff: string
    soType: string
    soDate: Date | null
    shippingInstructions: string
    email: string
    remarks: Nullable<GetSupplyOrderRemarksResponseType['data'][number]>
    origin: Nullable<GetAllLocationsResponseType[number]>
    destination: Nullable<GetAllLocationsResponseType[number]>
    createdBy?: string
    status?: string
    supplyOrderId?: number
  }
}

const initialState: OutboundSOAddProductSliceType = {
  isLoading: false,
  checkedIds: [],
  selectedItems: {},
  locationId: 0,
  query: {
    pageIndex: 0,
    pageSize: 20,
  },
  data: [],
  createSO: {
    poReff: null,
    soReff: '',
    soType: '',
    soDate: null,
    shippingInstructions: '',
    email: '',
    remarks: null,
    origin: null,
    destination: null,
  },
}

const outboundSOAddProductSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    setCreateSO: (state, { payload }: PayloadAction<OutboundSOAddProductSliceType['createSO']>) => {
      state.createSO = payload
      state.locationId = payload.origin?.location_id || 0
    },
    setCheckItem: (state, { payload }: PayloadAction<string>) => {
      const index = state.checkedIds.indexOf(payload)

      if (index >= 0) {
        state.checkedIds.splice(index, 1)
        delete state.selectedItems[payload]
      } else {
        state.checkedIds.push(payload)
      }
    },
    setCheckAll: (state, { payload }: PayloadAction<string[]>) => {
      state.checkedIds = Array.from(new Set([...state.checkedIds, ...payload]))
    },
    setUncheckAll: (state, { payload }: PayloadAction<string[]>) => {
      state.checkedIds = state.checkedIds.filter((id) => !payload.includes(id))
    },
    setItemQty: (
      state,
      { payload }: PayloadAction<{ productUniqueKey: string; item: ProductItemType }>,
    ) => {
      const { productUniqueKey, item } = payload

      if (item.productRequestQuantity) {
        state.selectedItems[productUniqueKey] = item
        if (!state.checkedIds.includes(productUniqueKey)) {
          state.checkedIds.push(productUniqueKey)
        }
      } else {
        delete state.selectedItems[productUniqueKey]
        state.checkedIds = state.checkedIds.filter((id) => id !== productUniqueKey)
      }
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<OutboundSOAddProductSliceType>) => {
      builder.addCase(fetchGetSOProduct.pending, (state, action) => {
        state.query = {
          ...state.query,
          ...action.meta.arg.params,
        }
      })
      builder.addCase(fetchGetSOProduct.fulfilled, (state, { payload }) => {
        state.data = payload.data.content
      })
    },
    [fetchGetSOProduct, fetchPutSupplyOrderDetail, fetchPostSupplyOrderDetail],
  ),
})

export const { reset, setCreateSO, setCheckItem, setCheckAll, setUncheckAll, setItemQty } =
  outboundSOAddProductSlice.actions
export default outboundSOAddProductSlice.reducer
